import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, Users, Eye, UserPlus } from 'lucide-react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ControlPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [userLogs, setUserLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }

    fetchAnalytics();
    fetchUserLogs();
  }, [user, navigate]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Errore caricamento analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/user-logs`, {
        withCredentials: true
      });
      setUserLogs(response.data);
    } catch (error) {
      console.error('Errore caricamento log');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const chartData = analytics?.daily_views?.map(item => ({
    date: new Date(item._id).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
    visite: item.count
  })) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft size={18} />
                Torna al Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">📊 Pannello di Controllo</h1>
                <p className="text-sm text-slate-600">Analytics e Monitoraggio Sito Web</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700 font-semibold">Visite Totali</p>
                  <p className="text-4xl font-bold text-blue-600">{analytics?.total_views || 0}</p>
                  <p className="text-xs text-slate-600 mt-1">Tutte le visite al sito</p>
                </div>
                <Eye className="text-blue-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 font-semibold">Visitatori Unici</p>
                  <p className="text-4xl font-bold text-green-600">{analytics?.unique_visitors || 0}</p>
                  <p className="text-xs text-slate-600 mt-1">Indirizzi IP distinti</p>
                </div>
                <Users className="text-green-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700 font-semibold">Visite (7gg)</p>
                  <p className="text-4xl font-bold text-orange-600">{analytics?.views_last_7_days || 0}</p>
                  <p className="text-xs text-slate-600 mt-1">Ultimi 7 giorni</p>
                </div>
                <TrendingUp className="text-orange-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700 font-semibold">Registrazioni</p>
                  <p className="text-4xl font-bold text-purple-600">{analytics?.total_registrations || 0}</p>
                  <p className="text-xs text-slate-600 mt-1">Clienti registrati</p>
                </div>
                <UserPlus className="text-purple-600" size={40} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>📈 Trend Visite Giornaliere (Ultimi 30 Giorni)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    label={{ value: 'Data', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    label={{ value: 'Numero Visite', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="visite" 
                    stroke="#ea580c" 
                    strokeWidth={3}
                    dot={{ fill: '#ea580c', r: 5 }}
                    activeDot={{ r: 8 }}
                    name="Visite Giornaliere"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* User Login Logs */}
        <Card>
          <CardHeader>
            <CardTitle>🔐 Log Accessi Utenti (Ultimi 100)</CardTitle>
            <p className="text-sm text-slate-600 mt-2">
              Storico completo di tutti gli accessi al sito
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Email Utente</th>
                    <th className="text-left py-3 px-4">Data/Ora</th>
                    <th className="text-left py-3 px-4">IP</th>
                    <th className="text-left py-3 px-4">Browser/Dispositivo</th>
                  </tr>
                </thead>
                <tbody>
                  {userLogs.map((log) => (
                    <tr key={log.log_id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{log.user_email}</span>
                          {log.user_email.includes('edilvale') && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Admin</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {new Date(log.login_time).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono">{log.ip_address || 'N/A'}</td>
                      <td className="py-3 px-4 text-xs text-slate-600 max-w-xs truncate">
                        {log.user_agent ? log.user_agent.substring(0, 60) + '...' : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ControlPanel;
